import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import Reveal from '@/components/Reveal';
import SEO from '@/components/SEO';
import { SectionLabel, PrimaryButton } from '@/components/Button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: '', organisation: '', role: '', email: '', message: '' };

export default function Contact() {
  const { t, i18n } = useTranslation(['contact', 'common']);
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!form.name.trim()) return t('errors.name');
    if (!form.organisation.trim()) return t('errors.organisation');
    if (!form.role.trim()) return t('errors.role');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t('errors.email');
    if (form.message.trim().length < 20) return t('errors.message');
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setStatus('error');
      setErrorMsg(err);
      return;
    }
    setStatus('submitting');
    setErrorMsg('');

    // Read the language fresh on every submit. Priority chain:
    //   1. live i18next instance (always the latest after a switcher click)
    //   2. localStorage (covers any race / navigation edge cases)
    //   3. 'en' fallback per spec (English is the default).
    const stored =
      typeof window !== 'undefined' ? window.localStorage.getItem('i18nextLng') : null;
    const language = (i18n.language || stored || 'en')
      .slice(0, 2)
      .toLowerCase();

    // eslint-disable-next-line no-console
    console.info('[contact] submitting with language=', language);

    try {
      const res = await axios.post(
        `${API}/contact`,
        // Send the new authoritative `language` field plus `lang` for
        // backward compatibility with any in-flight client builds / proxies.
        { ...form, language, lang: language },
        { timeout: 15000, validateStatus: () => true }
      );
      if (res.status === 200 && res.data && res.data.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMsg(t('errors.generic'));
      }
    } catch (e) {
      setStatus('error');
      setErrorMsg(t('errors.generic'));
    }
  };

  return (
    <Layout>
      <SEO title={t('seo.title')} description={t('seo.description')} />

      <section className="pt-32 md:pt-40 pb-10" data-testid="contact-hero">
        <div className="max-w-content mx-auto px-6 lg:px-10 max-w-4xl">
          <Reveal>
            <SectionLabel>{t('hero.eyebrow')}</SectionLabel>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-primary leading-[1.05] tracking-tight">
              {t('hero.headline')}
            </h1>
            <p className="mt-7 text-[17px] md:text-lg text-brand-text leading-[1.8] max-w-3xl">
              {t('hero.body')}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 md:py-20" data-testid="contact-form-section">
        <div className="max-w-content mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              {status === 'success' ? (
                <div className="p-10 md:p-14 border border-brand-accent/40 bg-brand-surface" data-testid="contact-success">
                  <p className="eyebrow text-brand-accent">{t('success.label')}</p>
                  <p className="mt-4 font-display text-2xl md:text-3xl text-brand-primary leading-snug">
                    {t('success.body')}
                  </p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
                  <Field label={t('form.name')} name="name" value={form.name} onChange={onChange} testId="contact-input-name" required />
                  <Field label={t('form.organisation')} name="organisation" value={form.organisation} onChange={onChange} testId="contact-input-organisation" required />
                  <Field label={t('form.role')} name="role" value={form.role} onChange={onChange} testId="contact-input-role" required />
                  <Field label={t('form.email')} name="email" type="email" value={form.email} onChange={onChange} testId="contact-input-email" required />
                  <div>
                    <label className="eyebrow text-brand-accent block mb-3" htmlFor="message">
                      {t('form.message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      value={form.message}
                      onChange={onChange}
                      required
                      minLength={20}
                      className="w-full bg-transparent border-b border-black/20 focus:border-brand-accent outline-none py-3 text-brand-primary placeholder:text-brand-muted text-[16px] leading-relaxed resize-none transition-colors"
                      placeholder={t('form.messagePlaceholder')}
                      data-testid="contact-input-message"
                    />
                  </div>

                  {status === 'error' && errorMsg && (
                    <p className="text-sm text-red-700" data-testid="contact-error">
                      {errorMsg}
                    </p>
                  )}

                  <div className="pt-4">
                    <PrimaryButton
                      type="submit"
                      testId="contact-submit"
                      onClick={() => {}}
                      className={status === 'submitting' ? 'opacity-60 pointer-events-none' : ''}
                    >
                      {status === 'submitting' ? t('form.sending') : t('form.submit')}
                    </PrimaryButton>
                  </div>
                </form>
              )}
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="p-8 md:p-10 bg-brand-surface border border-black/5 space-y-6" data-testid="contact-sidebar">
                <div>
                  <p className="eyebrow text-brand-accent">{t('sidebar.email')}</p>
                  <div className="mt-3 space-y-2">
                    <a
                      href="mailto:contact@jpeakeconsulting.co.uk"
                      className="block text-brand-primary link-underline text-[17px] font-medium"
                      data-testid="contact-email-primary"
                    >
                      contact@jpeakeconsulting.co.uk
                    </a>
                  </div>
                </div>

                <div className="border-t border-black/10 pt-6">
                  <p className="eyebrow text-brand-accent">{t('sidebar.linkedin')}</p>
                  <a
                    href="https://www.linkedin.com/in/jonathandpeake/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 block text-brand-primary link-underline text-[15px]"
                    data-testid="contact-linkedin"
                  >
                    linkedin.com/in/jonathandpeake
                  </a>
                </div>

                <div className="border-t border-black/10 pt-6">
                  <p className="eyebrow text-brand-accent">{t('sidebar.responseTime')}</p>
                  <p className="mt-3 text-[15px] text-brand-text leading-relaxed">
                    {t('sidebar.responseBody')}
                  </p>
                </div>

                <div className="border-t border-black/10 pt-6">
                  <p className="eyebrow text-brand-accent">{t('sidebar.confidentiality')}</p>
                  <p className="mt-3 text-[15px] text-brand-text leading-relaxed">
                    {t('sidebar.confidentialityBody')}
                  </p>
                </div>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, name, type = 'text', value, onChange, required, testId }) {
  return (
    <div>
      <label className="eyebrow text-brand-accent block mb-3" htmlFor={name}>
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-black/20 focus:border-brand-accent outline-none py-3 text-brand-primary placeholder:text-brand-muted text-[16px] transition-colors"
        data-testid={testId}
      />
    </div>
  );
}
